import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import Users from './users'
import Transactions from './transactions'
import type { IResolvedCapabilities } from 'modules/fund/capabilities'
import Loans, { DEFAULT_LOAN_SORT } from './loans'
import style from './tabs.module.scss'
import classNames from 'classnames'
import posthog from 'posthog-js'
import { InfiniteData } from 'react-query'
import {
    useLocation,
    useNavigationType,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import useGetTransactions from './hooks/useGetTransactions'
import useGetLoans from './hooks/useGetLoans'
import { useEffect, useMemo, useRef } from 'react'
import {
    ENTRY_SCROLL,
    readEntry,
    useEntryKey,
    useEntryScroll,
    useEntryState,
} from 'utilities/navigation/entryState'
import {
    TFundLoanSort,
    TFundLoanStatus,
    TFundTransactionType,
} from 'services/fund'
import { IFundMemberFilter, readFundTransactionsState } from './memberFilter'
interface ICustomTabsProps {
    data: InfiniteData<IGetMoneyPoolUsers>
    isLoading: boolean
    onWatchEnd: () => void
    shareValueAmount: number
    isManager: boolean
    /** every member's loans + the loans' sort/filter; defaults to isManager */
    canViewFinance?: boolean
    /** the viewer's capabilities — lets a «بانی» manage their own records */
    capabilities?: IResolvedCapabilities
    setIsOpenPayToOtherModal: React.Dispatch<React.SetStateAction<boolean>>
    setSelectUser: React.Dispatch<
        React.SetStateAction<IMoneyPoolResults | undefined>
    >
    activeMemberCount: number
    transactionsCount: number
    loansCount: number
    setSearchUser: React.Dispatch<React.SetStateAction<string>>
    search: string
    setSearch: React.Dispatch<React.SetStateAction<string>>
    refetch: () => void
    hiddenLoans?: boolean
}
type TFundTab = 'home' | 'profile' | 'longer-tab'

/**
 * `?tab=transactions|loans|members` — a notification's button naming the tab
 * it opens («🧾 مشاهدهٔ تراکنش», «واریز و برداشت صندوق»).
 */
const TAB_FROM_QUERY: Record<string, TFundTab> = {
    members: 'home',
    transactions: 'profile',
    loans: 'longer-tab',
}

// The returns already counted. The fund page unmounts these tabs while a
// member search loads, and the remount is no second return: react-router
// makes a new location object per navigation, so it is what identifies one.
const reportedReturns = new WeakSet<object>()
// The same, for the arrivals that opened this list narrowed to one member.
const reportedMemberFilters = new WeakSet<object>()

// What the event calls each tab; the eventKeys are react-bootstrap's demo
// names and mean nothing to someone reading a funnel.
const TAB_NAMES: Record<TFundTab, string> = {
    home: 'members',
    profile: 'transactions',
    'longer-tab': 'loans',
}

/**
 * A tab's name with its count as a badge. The count used to trail the name in
 * brackets — «تراکنش‌ها (۱۹۲۲)» — where it carried the same weight as the word
 * and the active tab differed from the others by an underline alone.
 */
function TabTitle({
    label,
    count,
    active,
}: {
    label: string
    count?: number
    active: boolean
}) {
    return (
        <span className="flex items-center justify-center gap-1.5">
            <span>{label}</span>
            {count ? (
                <span
                    className={classNames(
                        'min-w-[22px] rounded-full px-1.5 text-center text-[11px] font-bold leading-[18px]',
                        active
                            ? 'bg-brand-soft text-brand-deep'
                            : 'bg-sunken text-text-secondary'
                    )}
                >
                    {count.toLocaleString()}
                </span>
            ) : null}
        </span>
    )
}

export default function CustomTabs({
    data,
    isLoading,
    onWatchEnd,
    shareValueAmount,
    isManager,
    canViewFinance: canViewFinanceProp,
    capabilities,
    setIsOpenPayToOtherModal,
    setSelectUser,
    activeMemberCount,
    transactionsCount,
    loansCount,
    setSearchUser,
    search,
    setSearch,
    refetch,
    hiddenLoans,
}: ICustomTabsProps) {
    // a «بانی» with view_finance gets the manager's loans list
    const canViewFinance = canViewFinanceProp ?? isManager
    // Everything a member chose on this screen — the tab, the loans' order
    // and filter, the searches — is kept per history entry, so back from a
    // loan or a member lands on the list they left rather than on اعضا at the
    // top. See utilities/navigation/entryState.
    const entry = useEntryKey()
    const location = useLocation()
    const navigationType = useNavigationType()
    // Arriving from a کارنامهٔ عضو's «همهٔ تراکنش‌های این عضو»: the list opens
    // on تراکنش‌ها, narrowed to that member, until the chip is removed.
    const arrival = useMemo(
        () => readFundTransactionsState(location.state),
        [location.state]
    )
    const [memberFilter, setMemberFilter] =
        useEntryState<IFundMemberFilter | null>(
            'fund.memberFilter',
            arrival?.memberFilter ?? null
        )
    const [searchParams] = useSearchParams()
    const linkedTab = TAB_FROM_QUERY[searchParams.get('tab') ?? '']
    // `?tx=in-123`: the one transaction that link is about
    const focusTransaction = searchParams.get('tx')
    const [activeTab, setActiveTab] = useEntryState<TFundTab>(
        'fund.tab',
        arrival ? 'profile' : linkedTab ?? 'home'
    )
    const topOfTabs = useRef<HTMLDivElement>(null)
    const [searchTransaction, setSearchtransaction] = useEntryState<string>(
        'fund.transactionSearch',
        ''
    )
    const [searchTrans, setSearchTrans] = useEntryState<string>(
        'fund.transactionSearchText',
        ''
    )
    const [transactionType, setTransactionType] =
        useEntryState<TFundTransactionType>('fund.transactionType', '')
    const [searchLoans, setSearchLoans] = useEntryState<string>(
        'fund.loanSearch',
        ' '
    )
    const [searchLoan, setSearchLoan] = useEntryState<string>(
        'fund.loanSearchText',
        ' '
    )
    const [loanSort, setLoanSort] = useEntryState<TFundLoanSort>(
        'fund.loanSort',
        DEFAULT_LOAN_SORT
    )
    const [loanStatus, setLoanStatus] = useEntryState<TFundLoanStatus>(
        'fund.loanStatus',
        ''
    )
    const { id } = useParams()
    const {
        data: transactionData,
        isLoading: isLoadingTransactions,
        fetchNextPage: fetchNextTransactions,
        hasNextPage: hasMoreTransactions,
        isFetchingNextPage: isFetchingMoreTransactions,
    } = useGetTransactions(
        id,
        searchTransaction,
        transactionType,
        memberFilter?.poolship ?? null
    )
    const {
        data: LoansData,
        isLoading: isLoadingLoans,
        fetchNextPage,
        isError,
        // ترتیب و فیلتر are the manager's; a member's list keeps its order
    } = useGetLoans(
        id,
        searchLoans,
        canViewFinance ? loanSort : undefined,
        canViewFinance ? loanStatus : ''
    )

    // The page is back where it was once the list the member left is on it
    // again — from the react-query cache, so normally on this very render.
    useEntryScroll(
        activeTab === 'longer-tab'
            ? !!LoansData
            : activeTab === 'profile'
            ? !!transactionData
            : !!data
    )

    // Read once, at mount: had this entry been left on a tab before?
    const returnedTo = useRef(
        navigationType === 'POP'
            ? readEntry<TFundTab>(entry, 'fund.tab')
            : undefined
    )
    useEffect(() => {
        // How often back lands on a list other than اعضا — the case that used
        // to reset — and whether it came back scrolled into it.
        if (!returnedTo.current || reportedReturns.has(location)) return
        reportedReturns.add(location)
        posthog.capture('fund_detail_returned', {
            moneypool_id: Number(id),
            tab: TAB_NAMES[returnedTo.current],
            scrolled: (readEntry<number>(entry, ENTRY_SCROLL) ?? 0) > 0,
        })
        // once per return
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // What «همهٔ تراکنش‌های این عضو» found — the step after the tap on the
    // کارنامه, and the one that says whether the list it opens was worth
    // opening. Once per arrival: back to this entry is not a second one (POP),
    // and neither is the remount a member search causes (the WeakSet).
    const firstTransactionsPage = transactionData?.pages?.[0]?.results
    const foundForMember =
        firstTransactionsPage?.pagination?.count ??
        firstTransactionsPage?.result?.length
    useEffect(() => {
        if (!arrival || navigationType === 'POP') return
        // cleared before the first page came back: the count is the fund's
        if (memberFilter?.poolship !== arrival.memberFilter.poolship) return
        if (foundForMember === undefined || reportedMemberFilters.has(location))
            return
        reportedMemberFilters.add(location)
        posthog.capture('fund_transactions_member_filtered', {
            moneypool_id: Number(id),
            poolship_id: arrival.memberFilter.poolship,
            source: arrival.source,
            result_count: foundForMember,
            is_empty: foundForMember === 0,
        })
    }, [arrival, navigationType, memberFilter, foundForMember, location, id])

    const onClearMemberFilter = () => {
        posthog.capture('fund_transactions_member_filter_cleared', {
            moneypool_id: Number(id),
            poolship_id: memberFilter?.poolship,
        })
        setMemberFilter(null)
    }

    useEffect(() => {
        if (searchTrans === '') {
            setSearchtransaction('')
        }
    }, [searchTrans, setSearchtransaction])

    const onSelectTab = (key: string | null) => {
        if (!key || key === activeTab) return
        posthog.capture('fund_tab_selected', {
            moneypool_id: Number(id),
            tab: TAB_NAMES[key as TFundTab],
        })
        setActiveTab(key as TFundTab)
        // The bar stays pinned while a list scrolls under it, so a tab can be
        // chosen from deep inside another. Start the new list at its top
        // instead of wherever the old one happened to be.
        const top = topOfTabs.current
        if (top && top.getBoundingClientRect().top < 0) {
            top.scrollIntoView()
        }
    }

    useEffect(() => {
        if (searchLoan === '') {
            setSearchLoans('')
        }
    }, [searchLoan, setSearchLoans])

    // Tapping a #hashtag in a transaction's description filters this list by
    // it. The server matches `q` against the description column as well as the
    // member name/phone it already searched, so the hashtag — '#' and all — is
    // simply what `q` becomes; no separate endpoint is involved.
    const [hashtagFilter, setHashtagFilter] = useEntryState<string>(
        'fund.hashtagFilter',
        ''
    )

    const onHashtagClick = (hashtag: string) => {
        // The tag itself never leaves the device: managers file transactions
        // under names like «#وام_رضایی», which is as personal as the
        // description it sits in. Its length is enough to tell a real tag from
        // a stray '#'.
        posthog.capture('transaction_hashtag_search_used', {
            moneypool_id: Number(id),
            source: 'transaction_detail',
            tag_length: hashtag.length,
        })
        setHashtagFilter(hashtag)
        setSearchtransaction(hashtag)
        // Keep the free-text search box (which shares this query) in step, so
        // clearing it clears the hashtag too rather than leaving a filter the
        // box no longer shows.
        setSearchTrans(hashtag)
    }

    // Typing over a hashtag makes it an ordinary search: the «تراکنش‌های #…»
    // banner must not keep announcing a filter the box no longer holds.
    const onTransactionSearchChange = (text: string) => {
        setSearchTrans(text)
        if (hashtagFilter !== '' && text !== hashtagFilter) {
            setHashtagFilter('')
        }
    }

    const onClearHashtag = () => {
        posthog.capture('transaction_hashtag_filter_cleared', {
            moneypool_id: Number(id),
        })
        setHashtagFilter('')
        setSearchtransaction('')
        setSearchTrans('')
    }

    return (
        <>
            {/* where a tab's list begins — see onSelectTab */}
            <div ref={topOfTabs} />
            {data && (
                <Tabs
                    activeKey={activeTab}
                    onSelect={onSelectTab}
                    id="mp-detail-tabs"
                    className={classNames(
                        // Pinned: the lists under it run to hundreds of rows,
                        // and the way out of one should not be a scroll back
                        // to the top. `sticky`, not `fixed` — FundLayout's
                        // motion.div carries a transform, which re-anchors
                        // fixed children to the page but leaves sticky alone.
                        'sticky top-0 z-20 mb-0 border-b border-divider bg-white',
                        '[&_.nav-link]:!text-text-secondary [&_.nav-link]:!font-normal [&_.nav-link]:!border-0 [&_.nav-link]:!border-b-2 [&_.nav-link]:!border-transparent [&_.nav-link]:!text-sm [&_.nav-link]:!min-h-[44px] [&_.nav-link]:!flex [&_.nav-link]:!items-center [&_.nav-link]:!justify-center [&_.nav-link]:!px-1',
                        '[&_.nav-link.active]:!text-brand-deep [&_.nav-link.active]:!font-bold [&_.nav-link.active]:!border-green-1',
                        style.myTabs
                    )}
                    fill
                    variant="underline"
                >
                    <Tab
                        eventKey="home"
                        title={
                            <TabTitle
                                label="اعضا"
                                count={activeMemberCount}
                                active={activeTab === 'home'}
                            />
                        }
                    >
                        <Users
                            data={data}
                            isLoading={isLoading}
                            onWatchEnd={onWatchEnd}
                            shareValueAmount={shareValueAmount}
                            isManager={isManager}
                            setIsOpenPayToOtherModal={setIsOpenPayToOtherModal}
                            setSelectUser={setSelectUser}
                            setSearchUser={setSearchUser}
                            setSearch={setSearch}
                            search={search}
                            refetch={refetch}
                            isActive={activeTab === 'home'}
                        />
                    </Tab>
                    <Tab
                        eventKey="profile"
                        title={
                            <TabTitle
                                label="تراکنش‌ها"
                                count={transactionsCount}
                                active={activeTab === 'profile'}
                            />
                        }
                    >
                        <Transactions
                            data={transactionData}
                            isLoading={isLoadingTransactions}
                            isManager={isManager}
                            capabilities={capabilities}
                            isActive={activeTab === 'profile'}
                            fetchNextPage={fetchNextTransactions}
                            hasNextPage={!!hasMoreTransactions}
                            isFetchingNextPage={isFetchingMoreTransactions}
                            focusKey={focusTransaction}
                            searchTrans={searchTrans}
                            onSearchChange={onTransactionSearchChange}
                            appliedSearch={searchTransaction}
                            type={transactionType}
                            onTypeChange={setTransactionType}
                            setSearchtransaction={setSearchtransaction}
                            hashtagFilter={hashtagFilter}
                            onHashtagClick={onHashtagClick}
                            onClearHashtag={onClearHashtag}
                            memberFilter={memberFilter}
                            onClearMemberFilter={onClearMemberFilter}
                        />
                    </Tab>
                    <Tab
                        eventKey="longer-tab"
                        title={
                            <TabTitle
                                label="وام‌ها"
                                count={loansCount}
                                active={activeTab === 'longer-tab'}
                            />
                        }
                    >
                        <Loans
                            data={LoansData}
                            isLoading={isLoadingLoans}
                            fetchNextPage={fetchNextPage}
                            isError={isError}
                            setSearchLoans={setSearchLoans}
                            searchLoan={searchLoan}
                            setSearchLoan={setSearchLoan}
                            isManager={canViewFinance}
                            isHiddenLoans={hiddenLoans}
                            sort={loanSort}
                            onSortChange={setLoanSort}
                            status={loanStatus}
                            onStatusChange={setLoanStatus}
                            isActive={activeTab === 'longer-tab'}
                        />
                    </Tab>
                </Tabs>
            )}
        </>
    )
}
